import CheckIcon from "@assets/icons/check.svg";
import CopyIcon from "@assets/icons/duplicate.svg";
import InfoIcon from "@assets/icons/info.svg";
import { useI18n } from "@core/i18n/i18n";
import { UnregisteredPtInviteData } from "@domain/healthcare-team/healthcare-team";
import { sendAnalyticsEventInviteUnregisteredColleague } from "@domain/healthcare-team/healthcare-team-analytics";
import { Patient } from "@domain/patient/patient";
import { User } from "@domain/user/user";
import styled from "@emotion/styled";
import { Button } from "@ui/components/buttons/button";
import { BaseDialog } from "@ui/components/dialog/base-dialog";
import { RoutesPath } from "@ui/navigation/routes";
import { spacings } from "@ui/style";
import { colors } from "@ui/style/colors";
import { formatName } from "@utils/string-utils";
import { DialogProps } from "primereact/dialog";
import React, { useCallback, useMemo, useState } from "react";

export interface InviteUnregisteredPtDialogProps extends DialogProps {
	patient: Patient;
	invitedPt: UnregisteredPtInviteData;
	invitingPt: User;
}

export const InviteUnregisteredPtDialog = ({
	patient,
	invitedPt,
	invitingPt,
	...otherProps
}: InviteUnregisteredPtDialogProps): JSX.Element => {
	const { format } = useI18n();
	const [hasCopied, setHasCopied] = useState(false);

	const emailBody = useMemo(
		() =>
			format("patientDetails.healthcareTeam.inviteDialog.unregisteredPt.email.body", {
				patientName: formatName(patient),
				inviteUrl: `${location.protocol}//${location.host}${RoutesPath.Register}?invitationId=${invitedPt.invitationId}&countryCode=${invitingPt.country}`,
				invitingPtName: formatName(invitingPt),
			}),
		[format, invitedPt.invitationId, invitingPt, patient],
	);

	const emailSubject = useMemo(
		() =>
			format("patientDetails.healthcareTeam.inviteDialog.unregisteredPt.email.subject", {
				invitingPt: formatName(invitingPt),
			}),
		[format, invitingPt],
	);

	const mailToLink = useMemo(
		() => `mailto:${invitedPt.email}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`,
		[emailBody, emailSubject, invitedPt.email],
	);

	const copyInvite = useCallback(() => {
		setHasCopied(true);
		navigator.clipboard.writeText(emailBody);
		sendAnalyticsEventInviteUnregisteredColleague(patient, "copyCTA");
		setTimeout(() => setHasCopied(false), 3000);
	}, [emailBody, patient]);

	const sendByEmailClicked = useCallback(
		() => sendAnalyticsEventInviteUnregisteredColleague(patient, "sendByEmailCTA"),

		[patient],
	);

	// TODO: I had to give the button a children instead of using label and icon props because the iconPos props doesn't seem to work
	// We should check why this prop doesn't work (maybe update prime react)
	const copyButtonContent = (
		<CopyButtonContent>
			{hasCopied ? (
				<>
					{format("patientDetails.healthcareTeam.inviteDialog.unregisteredPt.copyButton.copied")}
					<CheckIcon width={24} height={24} />
				</>
			) : (
				<>
					{format("patientDetails.healthcareTeam.inviteDialog.unregisteredPt.copyButton")}
					<CopyIcon width={24} height={24} />
				</>
			)}
		</CopyButtonContent>
	) as unknown as string;

	return (
		<Dialog header={format("patientDetails.healthcareTeam.inviteDialog.header")} {...otherProps}>
			<InfoBox>
				<StyledInfoIcon height={24} width={24} />
				<InfoText>
					{format("patientDetails.healthcareTeam.inviteDialog.unregisteredPt.info", { email: invitedPt.email })}
				</InfoText>
			</InfoBox>

			<InstructionContainer className="p-size-m-bold">
				{format("patientDetails.healthcareTeam.inviteDialog.unregisteredPt.instruction")}
			</InstructionContainer>

			<Invitation>
				<MessageContainer>{emailBody}</MessageContainer>
				<ShareButtonContainer>
					<a href={mailToLink}>
						<Button
							label={format("patientDetails.healthcareTeam.inviteDialog.unregisteredPt.sendButton")}
							uiStyle="primary"
							onClick={sendByEmailClicked}
						/>
					</a>
					<Button uiStyle="secondary_B" onClick={copyInvite}>
						{copyButtonContent}
					</Button>
				</ShareButtonContainer>
			</Invitation>
		</Dialog>
	);
};

const StyledInfoIcon = styled(InfoIcon)`
	flex-shrink: 0;
`;

const CopyButtonContent = styled.div`
	display: flex;
	gap: ${spacings.XS};
`;

const Invitation = styled.div`
	padding: ${spacings.M};
	padding-bottom: ${spacings.S};
	border: 1px solid ${colors.primary25};
	border-radius: 8px;
	display: flex;
	flex-direction: column;
	gap: ${spacings.M};
`;

const InfoText = styled.div`
	> span {
		overflow-wrap: anywhere;
	}
`;

const MessageContainer = styled.div`
	padding-right: ${spacings.XL};
	white-space: pre-line;
	color: ${colors.grey};
	overflow-wrap: break-word;
`;

const InfoBox = styled.div`
	color: ${colors.primaryDark50};
	background-color: ${colors.primary10};
	border: 1px solid ${colors.primary75};
	border-radius: 8px;
	display: flex;
	padding: ${spacings.S};
	gap: 8px;
`;

const ShareButtonContainer = styled.div`
	display: flex;
	gap: ${spacings.S};
`;

const InstructionContainer = styled.div`
	margin-top: ${spacings.M};
	margin-bottom: ${spacings.S};
`;

const Dialog = styled(BaseDialog)`
	width: 732px;
`;
